import React, { useState, useEffect } from 'react';
import UnderlineTitle from '../../../../utils/UnderlineTitle';
import InputRange from '../../../../utils/InputRange';
import InputOptions from '../../../../utils/InputOptions';
import { CurrencyFormat, cleanRutFormat } from '../../../../utils';
import Axios from 'axios';

import useAccessoriesData from '../../../hooks/useAccessoriesData';
import useConfiguratorData from '../../../hooks/useConfiguratorData';
import useFinanciadoData from '../../../hooks/useFinanciadoData';
import useLinksData from '../../../hooks/useLinksData';
import usePriceData from '../../../hooks/usePriceData';
import usePersoalData from '../../../hooks/usePersonalData';
import useDealershipsData from '../../../hooks/useDealershipsData';

const defaultProps = {
    r9: false,
    minDownpayment: 31,
    maxDownpayment: 80,
    minFeet: 12,
    maxFeet: 60
}

const r9Props = {
    r9: true,
    minDownpayment: 31,
    maxDownpayment: 40,
    minFees: 24,
    maxFees: 36
}

const defaultDataFinanciado = {
    vehicleAmount: 0,
    car_amount: 0,
    downpayment_percentage: 31,
    number_fees: 24,
    smart_buy: false,
    sap_code: 0,
}

const ModalFinanciadoBody = ({ simulateDataDefault, onCloseModal }) => {

    const { getAddedAccessories, getIgnoreAccessories } = useAccessoriesData();
    const { getSelectedModel, getSelectedVersion, getSummary, getSelectedPrepaidMaintenancePlan } = useConfiguratorData();
    const { setIgnoreFinanciado, setFinanciado, setSimulationChanged, getSimulateNumberFees, getSimulateDownpaymentPercentage, setSimulateDownpaymentPercentage, setSimulateNumberFees, setSimulatedCreditData, getPreApprovalData } = useFinanciadoData();
    const { getLinks } = useLinksData();
    const { getFinancedTotal } = usePriceData();
    const { getPersonalData } = usePersoalData();
    const { selectedDealership } = useDealershipsData();

    const addedAccessories = getAddedAccessories();
    const selectedModel = getSelectedModel();
    const selectedVersion = getSelectedVersion();
    const summary = getSummary();
    const links = getLinks();
    const selectedPrepaidMaintenancePlan = getSelectedPrepaidMaintenancePlan();
    const financedTotal = getFinancedTotal();
    const simulateNumberFees = getSimulateNumberFees();
    const simulateDownpaymentPercentage = getSimulateDownpaymentPercentage();

    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [defaultInputProps, setDefaultInputProps] = useState(r9Props);
    const [error, setError] = useState(null);

    const [simulateData, setSimulateData] = useState({ ...simulateDataDefault });

    useEffect(() => {
        let data = {
            ...simulateData,
            downpayment_percentage: parseFloat(simulateDownpaymentPercentage),
            number_fees: parseInt(simulateNumberFees),
        };
        simulateCredit({ ...data });
    }, [])

    const simulateCredit = (data) => {
        setLoading(true);
        setError(null);
        const cleanRut = cleanRutFormat(getPersonalData().rut);
        Axios.post(`/api/vehicles/bnp/simulate_credit/`, {
            ...data,
            // v1:
            car_amount: getFinancedTotal(true),
            full_name: getPersonalData().full_name,
            email: getPersonalData().email,
            rut: cleanRut,
            phone_number: getPersonalData().phone_number,
            option_code: selectedVersion.option_code ? selectedVersion.option_code : '',
            dealership_id: selectedDealership?.rut || '',
            model_id: selectedModel.model_id,
            // v2:
            vehicleAmount: getFinancedTotal(true),
            first_name: getPersonalData().first_name,
            last_name: getPersonalData().last_name,
            clientRut: cleanRut,
            phone: getPersonalData().phone_number,
            downPayment: Math.round((simulateDownpaymentPercentage / 100) * getFinancedTotal(true)),
            creditAmount: getFinancedTotal(true) - Math.round((simulateDownpaymentPercentage / 100) * getFinancedTotal(true)),
            smart_buy: simulateData.smart_buy,
        }).then(response => {
            let data = { ...response.data };
            setSimulationChanged(true);
            setSimulateData(prevState => ({
                ...prevState,
                credit_total_cost: data.credit_total_cost || data.totalCost,
                downpayment_amount: data.downpayment_amount || data.downPayment,
                equivalent_annual_load: data.equivalent_annual_load || data.cae,
                future_value_guaranteed: data.future_value_guaranteed || data.vfg,
                fee_value: data.fee_value || data.repaymentAmount,
                rate_interest: data.rate_interest,
                simulation_id: data.simulationId,
                rate: data.rate,
            }));
            setSimulatedCreditData(data)
            getPreApprovalData().total_cost = data.totalCost;
            getPreApprovalData().request_id = data.simulationId;
            getPreApprovalData().rate = data.rate;
        })
            .catch((error) => {
                console.error(error);

                if (error.response && error.response.status === 400) {
                    setError(error.response.data.message);
                } else {
                    setError('Algo salió mal, inténtalo de nuevo');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const handleChangeCheckboxR9 = e => {
        if (e.target.checked) setDefaultInputProps(r9Props);
        else setDefaultInputProps(defaultProps);
        let data = {
            ...simulateData,
            smart_buy: e.target.checked
        }
        simulateCredit(data)
    }

    const handleChangeDownpayment = value => {
        let data = {
            ...simulateData,
            downpayment_percentage: parseInt(value)
        }
        setSimulateDownpaymentPercentage(parseInt(value));
        setSimulateData(data);
        simulateCredit(data);
    }

    const handleChangeFees = value => {
        let data = {
            ...simulateData,
            number_fees: parseInt(value)
        }
        setSimulateNumberFees(parseInt(value));
        setSimulateData(data);
        simulateCredit(data);
    }

    const handleAccept = () => {
        setFinanciado({ ...simulateData });
        setIgnoreFinanciado(false);
        onCloseModal();
    }

    useEffect(() => {
        setIgnoreFinanciado(false);
    }, [])

    return (
        <div className="modal-box">
            <div className="close-button icon close" id="close-modal" onClick={onCloseModal}></div>
            <div className="modal-header">
                <div className="modal-title">
                    <h1>Simulación Financiamiento MAF</h1>
                    <p>Configura y encuentra el financiamiento ideal para tu Toyota.</p>
                </div>
            </div>
            <div className="modal-body">
                <div className="modal-financiado-container">
                    <div className={"plan-renueve " + (showDetails ? "show-details" : "")}>
                        <UnderlineTitle title={`${selectedModel.name} ${selectedVersion.name}`}>
                            <h2>{CurrencyFormat(selectedVersion.list_price)}</h2>
                        </UnderlineTitle>
                        {selectedVersion.brand_bonus ? (
                            <UnderlineTitle small title="Bono marca">
                                <h4>- {CurrencyFormat(selectedVersion.brand_bonus)}</h4>
                            </UnderlineTitle>
                        ) : null}
                        {selectedVersion.financing_bonus ? (
                            <UnderlineTitle small title="Bono por financiamiento">
                                <h4>- {CurrencyFormat(selectedVersion.financing_bonus)}</h4>
                            </UnderlineTitle>
                        ) : null}
                        {selectedVersion.smart_buy_bonus ? (
                            <UnderlineTitle small title="Bono por financiamiento Plan Renueve">
                                <h4>- {CurrencyFormat(selectedVersion.smart_buy_bonus)}</h4>
                            </UnderlineTitle>
                        ) : null}
                        {addedAccessories.length > 0 ? (
                            <UnderlineTitle small title={`${addedAccessories.length} ${addedAccessories.length > 1 ? 'Accesorios' : 'Accesorio'}`}>
                                <h4>{CurrencyFormat(summary.accessories)}</h4>
                            </UnderlineTitle>
                        ) : null}
                        {selectedPrepaidMaintenancePlan ? (
                            <UnderlineTitle title={selectedPrepaidMaintenancePlan.name}>
                                <h2>{CurrencyFormat(selectedPrepaidMaintenancePlan.price)}</h2>
                            </UnderlineTitle>
                        ) : null}
                        <div className="input-renueve">
                            {/* <label className="custom-checkbox">
                                    <input type="checkbox" checked={simulateData.smart_buy} onChange={() => {}}/>
                                    <span className="checkmark"></span>
                                </label> */}
                            <span>Simulación corresponde a <a href={links.financing_page ? links.financing_page : '#'} target="_blank">Plan Renueve</a> MAF Chile.</span>
                        </div>
                    </div>
                    <div className="underline-title total-label">
                        <h4>Total a financiar <span><a className="link-arrow" onClick={() => { setShowDetails(!showDetails) }}>{showDetails ? "Cerrar detalle" : "Ver detalle"} <i className="icon chevron-strong"></i></a></span></h4>
                        <div className="right-element">
                            <h2>{CurrencyFormat(financedTotal)}</h2>
                        </div>
                    </div>
                    <div className="inputs-container">
                        <InputRange
                            onChangeValue={handleChangeDownpayment}
                            title="Porcentaje de Pie"
                            subtitle={`Hasta un ${defaultInputProps.maxDownpayment}% del valor total del vehículo.`}
                            valueText="% Mínimo"
                            valueTextMax="% Máximo"
                            defaultValue={simulateDownpaymentPercentage}
                            suffix="%"
                            minValue={defaultInputProps.minDownpayment}
                            maxValue={defaultInputProps.maxDownpayment} />
                        <InputOptions
                            onChangeValue={handleChangeFees}
                            title="Cantidad de Cuotas"
                            subtitle="Seleccione en cuantas cuotas abonará su vehículo."
                            defaultValue={simulateNumberFees}
                            minValue={defaultInputProps.minFees}
                            maxValue={defaultInputProps.maxFees} />
                    </div>
                    <div className="summary-financiado">
                        {!loading && !error ?
                            <>
                                <UnderlineTitle title="Valor del Pie">
                                    <h2 className="title-financiado">{CurrencyFormat(simulateData.downpayment_amount)}</h2>
                                </UnderlineTitle>
                                <UnderlineTitle title="Costo Total del Crédito">
                                    <h2 className="title-financiado color-red">{CurrencyFormat(simulateData.credit_total_cost)} | CAE: {simulateData.equivalent_annual_load}%</h2>
                                </UnderlineTitle>
                                <UnderlineTitle title="Cuota Mensual">
                                    <h2 className="title-financiado">{CurrencyFormat(simulateData.fee_value)}</h2>
                                </UnderlineTitle>
                                {simulateData.smart_buy &&
                                    <UnderlineTitle title="Cuota Final de Plan Renueve">
                                        <h2 className="title-financiado">{CurrencyFormat(simulateData.future_value_guaranteed)}</h2>
                                    </UnderlineTitle>
                                }
                            </>
                            : error ?
                                <div className="error-message"><h2>{error}</h2></div>
                                :
                                <div className="spinner-container">
                                    <div className="spinner"></div>
                                </div>
                        }
                        <div className="disclaimer">
                            <p>Los valores y conceptos indicados en esta simulación son referenciales, no vinculantes y no conllevan la aprobación y otorgamiento del crédito, el cual es gestionado y otorgado por MAF Chile. El Valor Cuota incluye gastos operacionales e impuestos al crédito. Las cuales se encuentran calculadas con vencimiento en 30 días.</p>
                            <br />
                            <p>
                                El monto de la cuota para financiamiento automotriz es meramente referencial y no vinculante, los montos en pesos chilenos informados y demás condiciones del crédito están sujetas a variación en función del monto y plazo en que se tome el crédito. No considera meses de gracia o pago diferido. Los valores informados incluyen impuesto al crédito, gastos o cargos propios del crédito, no incluye cargos por productos o servicios voluntariamente contratados.
                            </p>
                        </div>
                    </div>
                </div>

            </div>
            <div className="modal-footer">
                <button className="btn black" onClick={handleAccept}>Aceptar</button>
            </div>
        </div>
    )
};

export default ModalFinanciadoBody;